import { useContext, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";

// @mui material components
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Icon from "@mui/material/Icon";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// Context
import { AuthContext } from "context";

// Services
import AuthService from "services/auth-service";
import JwtService from "services/jwt-service";

// Custom styles
import { styled } from "@mui/material/styles";

// Restore with a balanced approach - performance without breaking functionality
const TopNavbarContainer = styled(AppBar)({
  background: "linear-gradient(135deg, #f5f7fa 0%, #e4e7f1 100%)",
  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
  borderBottom: "1px solid rgba(0, 0, 0, 0.05)",
  padding: "8px 0",
  position: "sticky",
  top: 0,
  zIndex: 1100,
});

const NavTab = styled(IconButton)(({ active }) => ({
  color: active ? "#ffffff" : "#333333",
  fontSize: "0.875rem",
  fontWeight: active ? "600" : "500",
  textTransform: "none",
  margin: "0 8px",
  padding: "10px 20px",
  borderRadius: "50px",
  backgroundColor: active ? "#465475" : "#ffffff",
  boxShadow: active ? "0 2px 8px rgba(70, 84, 117, 0.2)" : "0 1px 4px rgba(0, 0, 0, 0.05)",
  transition: "none",
  "&:hover": {
    backgroundColor: active ? "#465475" : "#f0f2f5",
  },
}));

// Mobile menu item style
const MobileMenuItem = styled(MenuItem)({
  padding: "12px 20px",
  fontSize: "1rem",
  fontWeight: "500",
  "&:hover": {
    backgroundColor: "rgba(70, 84, 117, 0.1)",
  },
});

function TopNavbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const authContext = useContext(AuthContext);
  const [mobileMenuAnchor, setMobileMenuAnchor] = useState(null);
  
  // Get token from localStorage
  const token = localStorage.getItem("token");
  
  // Filter routes based on user authorities
  const filteredRoutes = useMemo(() => {
    const allRoutes = [
      { key: "financial-hub", name: "Financial Hub", path: "/financial-hub" },
      { key: "budget-planner", name: "Budget Planner", path: "/budget-planner" },
      { key: "user-profile", name: "User Profile", path: "/user-profile" },
      { key: "user-management", name: "User Management", path: "/user-management" }
    ];
    
    // Admins can see all routes (including user-profile)
    if (JwtService.isAdmin(token)) {
      return allRoutes;
    }
    
    // If user is not admin, hide User Management route
    return allRoutes.filter(route => route.key !== "user-management");
  }, [token]);

  const handleNavigate = (path) => {
    navigate(path);
    // Close mobile menu if open
    setMobileMenuAnchor(null);
  };
  
  const handleLogOut = async () => {
    await AuthService.logout();
    authContext.logout();
  };

  // Simple active detection
  const activeKey = (() => {
    const path = location.pathname;
    if (path.startsWith("/financial-hub")) return "financial-hub";
    if (path.startsWith("/budget-planner")) return "budget-planner";
    if (path.startsWith("/user-profile")) return "user-profile";
    if (path.startsWith("/user-management")) return "user-management";
    return "";
  })();

  // Handle mobile menu open/close
  const handleMobileMenuOpen = (event) => {
    setMobileMenuAnchor(event.currentTarget);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuAnchor(null);
  };

  return (
    <TopNavbarContainer position="static">
      <Toolbar sx={{ justifyContent: "center" }}>
        {/* Logo Section */}
        <MDBox display="flex" alignItems="center" mr={2}>
          <Icon sx={{ fontSize: "1.8rem", color: "#465475", mr: 1 }}>savings</Icon>
          <MDTypography
            component="h6"
            variant="h6"
            fontWeight="600"
            color="#465475"
            sx={{ fontSize: "1.2rem" }}
          >
            Trackwise
          </MDTypography>
        </MDBox>

        {/* Desktop Navigation - Hidden on small screens */}
        <MDBox 
          display={{ xs: "none", md: "flex" }} 
          alignItems="center" 
          sx={{ flexGrow: 0 }}
        >
          {filteredRoutes.map((route) => (
            <NavTab
              key={route.key}
              active={activeKey === route.key}
              onClick={() => handleNavigate(route.path)}
            >
              <span>{route.name}</span>
            </NavTab>
          ))}
        </MDBox>

        {/* Mobile Menu Button - Visible only on small screens */}
        <MDBox 
          display={{ xs: "flex", md: "none" }} 
          alignItems="center" 
          sx={{ flexGrow: 0 }}
        >
          <IconButton
            size="large"
            aria-label="navigation menu"
            aria-controls="menu-appbar"
            aria-haspopup="true"
            onClick={handleMobileMenuOpen}
            color="inherit"
            sx={{
              color: "#465475",
              backgroundColor: "rgba(70, 84, 117, 0.1)",
              borderRadius: "50%",
              width: 40,
              height: 40,
            }}
          >
            <Icon>menu</Icon>
          </IconButton>
          
          {/* Mobile Menu */}
          <Menu
            id="menu-appbar"
            anchorEl={mobileMenuAnchor}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "right",
            }}
            keepMounted
            transformOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
            open={Boolean(mobileMenuAnchor)}
            onClose={handleMobileMenuClose}
            sx={{
              mt: 1,
            }}
          >
            {filteredRoutes.map((route) => (
              <MobileMenuItem
                key={route.key}
                onClick={() => handleNavigate(route.path)}
                selected={activeKey === route.key}
                sx={{
                  backgroundColor: activeKey === route.key ? "rgba(70, 84, 117, 0.1)" : "transparent",
                  fontWeight: activeKey === route.key ? "600" : "500",
                }}
              >
                {route.name}
              </MobileMenuItem>
            ))}
          </Menu>
        </MDBox>

        {/* Logout Button */}
        <MDBox display="flex" alignItems="center" sx={{ flexGrow: 1, justifyContent: "flex-end" }}>
          <MDButton
            variant="gradient"
            color="info"
            onClick={handleLogOut}
            sx={{
              padding: "8px 16px",
              borderRadius: "50px",
              backgroundColor: "#465475",
              backgroundImage: "linear-gradient(135deg, #5a6a94 0%, #465475 100%)",
              fontWeight: "500",
              fontSize: "0.875rem",
              transition: "none",
            }}
          >
            Log Out
          </MDButton>
        </MDBox>
      </Toolbar>
    </TopNavbarContainer>
  );
}

export default TopNavbar;